<template>
  <div :id="'items-table' + tripNumber" class="items-table">
    <div class="items-table__mobile">
      <b-form-group
        label-cols-sm="3"
        label-align-sm="right"
        label-size="sm"
        label-for="filterInput"
        class="mb-0 d-none d-sm-block d-md-none d-block d-sm-none"
      >
        <div class="items-table__mobile__buttons">
          <div class="items-table__mobile__buttons__table">
            <b-overlay
              :show="deletingCommodity"
              rounded
              opacity="0.6"
              spinner-small
              class="d-inline-block"
              @hidden="onHidden('delete-commodity-button')"
            >
              <b-button
                ref="delete-commodity-button"
                :disabled="deletingCommodity"
                v-if="allowed"
                class="items-table__mobile__buttons__table--delete"
                size="sm"
                @click="deleteRows"
                >Delete</b-button
              >
            </b-overlay>
            <b-overlay
              :show="addingCommodity"
              rounded
              opacity="0.6"
              spinner-small
              class="d-inline-block"
              @hidden="onHidden('add-commodity-button')"
            >
              <b-button
                ref="add-commodity-button"
                :disabled="addingCommodity"
                v-if="allowed"
                class="items-table__mobile__buttons__table--add-row"
                size="sm"
                @click="addItem()"
                >Add new row</b-button
              >
            </b-overlay>
          </div>
        </div>
      </b-form-group>
    </div>
    <b-table
      show-empty
      hover
      small
      foot-clone
      class="items-table__table table-fixed"
      head-variant="light"
      stacked="md"
      fixed
      :items="items"
      :busy="isBusy"
      :fields="fields"
    >
      <template v-slot:table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner
            class="align-middle"
            aria-label="Loading table"
          ></b-spinner>
          <strong>Loading...</strong>
        </div>
      </template>
      <template v-slot:thead-top="data">
        <b-tr v-bind:key="data.columns">
          <b-th variant="dark" :colspan="fields.length">
            <h4 v-if="!allowed" class="text-left"><b>Details</b></h4>
            <div class="items-table__table__buttons-table">
              <div>
                <b-overlay
                  :show="deletingCommodity"
                  rounded
                  opacity="0.6"
                  spinner-small
                  class="d-inline-block"
                  @hidden="onHidden('delete-commodity-button')"
                >
                  <b-button
                    ref="delete-commodity-button"
                    :disabled="deletingCommodity"
                    v-if="allowed"
                    class="items-table__table__buttons-table--delete"
                    size="sm"
                    @click="deleteRows"
                    >Delete</b-button
                  >
                </b-overlay>
                <b-overlay
                  :show="addingCommodity"
                  rounded
                  opacity="0.6"
                  spinner-small
                  class="d-inline-block"
                  @hidden="onHidden('add-commodity-button')"
                >
                  <b-button
                    ref="add-commodity-button"
                    :disabled="addingCommodity"
                    v-if="allowed"
                    @click="addItem()"
                    class="items-table__table__buttons-table--add-row"
                    size="sm"
                    >Add new row</b-button
                  >
                </b-overlay>
              </div>
            </div>
          </b-th>
        </b-tr>
      </template>

      <template v-slot:head(actions)="column">
        <b-form-checkbox
          :id="column.index"
          v-model="areAllSelected"
          name="checkbox-1"
          value="accepted"
          unchecked-value="not_accepted"
          class="all-checkbox"
        >
        </b-form-checkbox>
      </template>

      <template v-slot:head(hu_count)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[1].label
        }}</span>
      </template>

      <template v-slot:head(dimensions)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[2].label
        }}</span>
      </template>

      <template v-slot:head(weight)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[3].label
        }}</span>
      </template>

      <template v-slot:head(contribution)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[4].label
        }}</span>
      </template>

      <template v-slot:head(nmfcCalculated)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[5].label
        }}</span>
      </template>

      <template v-slot:head(nmfc)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[6].label
        }}</span>
      </template>

      <template v-slot:head(description)="row">
        <span :key="row.label" class="items-table__table__header">{{
          fields[7].label
        }}</span>
      </template>

      <template v-slot:cell(actions)="row">
        <b-form-checkbox
          v-model="items[row.index].isChecked"
          name="select-row"
          value="accepted"
          unchecked-value="not_accepted"
          class="checkbox-item"
        >
        </b-form-checkbox>
      </template>

      <template v-slot:cell(hu_count)="row">
        <b-form-input
          :ref="`huCountInput${row.index}`"
          class="items-table__table--input"
          onClick="this.select();"
          v-model="items[row.index].hu_count"
          :title="items[row.index].hu_count"
          @keyup.enter="$refs[`huCountInput${row.index}`].blur()"
          @blur="updateCommodity(row.index, 'hu_count')"
          @change="validateItemsTable()"
          size="sm"
          list="hu_count"
          autocomplete="off"
        ></b-form-input>

        <datalist id="hu_count">
          <option v-for="(hc, index) in hu_count" v-bind:key="index">
            {{ hc }}
          </option>
        </datalist>
      </template>

      <template v-slot:cell(dimensions)="row">
        <b-form-input
          :ref="`dimensionsInput${row.index}`"
          class="items-table__table--input"
          v-model="items[row.index].dimensions"
          :title="items[row.index].dimensions"
          onClick="this.select();"
          @keyup.enter="$refs[`dimensionsInput${row.index}`].blur()"
          @blur="updateCommodity(row.index, 'dimensions')"
          @change="validateItemsTable()"
          size="sm"
          list="dimensions"
          autocomplete="off"
        ></b-form-input>

        <datalist id="dimensions">
          <option v-for="(dim, index) in dimensions" v-bind:key="index">
            {{ dim }}
          </option>
        </datalist>
      </template>

      <template v-slot:cell(weight)="row">
        <b-form-input
          :ref="`weightInput${row.index}`"
          class="items-table__table--input"
          v-model="items[row.index].weight"
          :title="items[row.index].weight"
          onClick="this.select();"
          @keyup.enter="$refs[`weightInput${row.index}`].blur()"
          @blur="updateCommodity(row.index, 'weight')"
          @change="validateItemsTable()"
          size="sm"
          list="weight"
          autocomplete="off"
        ></b-form-input>

        <datalist id="weight">
          <option v-for="(w, index) in weight" v-bind:key="index">
            {{ w }}
          </option>
        </datalist>
      </template>

      <template v-slot:cell(contribution)="row">
        <b-form-input
          readonly
          class="items-table__table--input"
          onClick="this.select();"
          v-model="items[row.index].contribution"
          :title="items[row.index].contribution"
          size="sm"
          list="contribution"
          autocomplete="off"
        ></b-form-input>

        <datalist id="contribution">
          <option v-for="(com, index) in contribution" v-bind:key="index">
            {{ com }}
          </option>
        </datalist>
      </template>

      <template v-slot:cell(nmfcCalculated)="row">
        <b-form-input
          :ref="`nmfcCalculatedInput${row.index}`"
          class="items-table__table--input"
          v-model="items[row.index].nmfcCalculated"
          disabled
          :title="items[row.index].nmfcCalculated"
          onClick="this.select();"
          @keyup.enter="$refs[`nmfcCalculatedInput${row.index}`].blur()"
          @blur="updateCommodity(row.index, 'nmfcCalculated')"
          @change="validateItemsTable()"
          size="sm"
          list="nmfcCalculated"
          autocomplete="off"
        ></b-form-input>

        <datalist id="nmfcCalculated">
          <option v-for="(com, index) in nmfcCalculated" v-bind:key="index">
            {{ com }}
          </option>
        </datalist>
      </template>

      <template v-slot:cell(nmfc)="row">
        <b-form-input
          :ref="`nmfcInput${row.index}`"
          class="items-table__table--input"
          v-model="items[row.index].nmfc"
          :title="items[row.index].nmfc"
          onClick="this.select();"
          @keyup.enter="$refs[`nmfcInput${row.index}`].blur()"
          @blur="updateCommodity(row.index, 'nmfc')"
          size="sm"
          list="nmfc"
          autocomplete="off"
        ></b-form-input>

        <datalist id="nmfc">
          <option v-for="(com, index) in nmfc" v-bind:key="index">
            {{ com }}
          </option>
        </datalist>
      </template>

      <template v-slot:cell(description)="row">
        <b-form-input
          :ref="`descriptionInput${row.index}`"
          class="items-table__table--input"
          v-model="items[row.index].description"
          :title="items[row.index].description"
          onClick="this.select();"
          @keyup.enter="$refs[`descriptionInput${row.index}`].blur()"
          @blur="updateCommodity(row.index, 'description')"
          size="sm"
          list="description"
          autocomplete="off"
        ></b-form-input>

        <datalist id="description">
          <option v-for="(com, index) in description" v-bind:key="index">
            {{ com }}
          </option>
        </datalist>
      </template>

      <template v-slot:foot(actions)="column">
        <b-form-input
          readonly
          class="items-table__table__footer--totals-title"
          :key="column.label"
          v-model="totalsTitle"
          size="sm"
          autocomplete="off"
        ></b-form-input>
      </template>

      <template v-slot:foot(hu_count)="column">
        <b-form-input
          readonly
          class="items-table__table__footer__total-hu--total"
          :key="column.label"
          v-model="totalHuCount"
          placeholder="Total hu count"
          size="sm"
          autocomplete="off"
        ></b-form-input>
      </template>

      <template v-slot:foot(dimensions)="column">
        <b-form-input
          readonly
          class="items-table__table__footer--total-volume"
          :key="column.label"
          v-model="volumeAndDensity.volume"
          placeholder="Total volume"
          size="sm"
          autocomplete="off"
        ></b-form-input>
      </template>

      <template v-slot:foot(weight)="column">
        <b-form-input
          ref="totalWeightInput"
          :key="column.label"
          v-model="totalWeight"
          onClick="this.select();"
          @keyup.enter="$refs.totalWeightInput.blur()"
          @blur="standardizeTotalWieght"
          class="items-table__table__footer--total-weight"
          placeholder="Total weight"
          size="sm"
          autocomplete="off"
        ></b-form-input>
      </template>

      <template v-slot:foot(contribution)="column">
        <b-form-input
          ref="markupUSDInput"
          v-model="markupUSD"
          title="Markup (USD)"
          onClick="this.select();"
          @keyup.enter="$refs.markupUSDInput.blur()"
          @blur="updateHaul"
          :key="column.label"
          type="number"
          class="items-table__table__footer--linear-feet"
          placeholder="Markup (USD)"
          size="sm"
          autocomplete="off"
        ></b-form-input>
      </template>

      <template v-slot:foot(nmfcCalculated)="column">
        <b-form-input
          ref="expectedCostInput"
          v-model="markupPercent"
          title="Markup %"
          onClick="this.select();"
          @keyup.enter="$refs.expectedCostInput.blur()"
          @blur="updateHaul"
          :key="column.label"
          type="number"
          class="items-table__table__footer--linear-feet"
          placeholder="Markup %"
          size="sm"
          autocomplete="off"
        ></b-form-input>
      </template>

      <template v-slot:foot(nmfc)="column">
        <b-input-group>
          <b-form-input
            disabled
            ref="linearFeetInput"
            v-model="haulInformation[tripNumber - 1].linear_feet"
            title="Linear Feet"
            onClick="this.select();"
            @keyup.enter="$refs.linearFeetInput.blur()"
            @blur="updateHaul"
            :key="column.label"
            type="number"
            class="items-table__table__footer--linear-feet"
            placeholder="Linear feet"
            size="sm"
            autocomplete="off"
          ></b-form-input>

          <b-input-group-append>
            <b-button
              :disabled="addingLinearFeet"
              @click="calculateLinearFeet()"
              class="calculate-linear-feet"
              variant="info"
              ><b-spinner v-if="addingLinearFeet" small></b-spinner
              ><i v-else class="fa fa-calculator"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </template>

      <template v-slot:foot(description)="column">
        <b-form-input
          ref="commercialValueInput"
          :key="column.label"
          v-model="haulInformation[tripNumber - 1].commercial_value"
          onClick="this.select();"
          @keyup.enter="$refs.commercialValueInput.blur()"
          @blur="updateHaul"
          title="Commercial Value"
          type="number"
          class="items-table__table__footer--commercial-value"
          placeholder="Commercial Value"
          size="sm"
          autocomplete="off"
        ></b-form-input>
        <div class="items-table__standardizer">
          <span
            v-if="!showItemsTableFlag"
            class="items-table__standardizer--warning-message"
            ><b-icon
              icon="exclamation-circle"
              animation="fade"
              variant="danger"
            ></b-icon>
            Please confirm
          </span>
          <b-button
            pill
            variant="success"
            @click="standardizeContributionAndNmfc()"
            v-b-tooltip.hover
            title="Confirm"
            class="items-table__standardizer-button"
          >
            <b-icon
              icon="clipboard-check"
              :animation="standardize ? 'fade' : ''"
              font-scale="0.8"
            ></b-icon
          ></b-button>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import fieldsTable from "@/utils/LTL/itemsTable";
import { removePointerEvents } from "@/mixins/pickupAndDelivery/standardizers";
import {
  getCalculateVolumeAndDensityMixin,
  getTotalHuCountMixin,
  getTotalWeightMixin,
  standardizeTotalWieghtMixin
} from "@/mixins/itemsTable/totals";
import { mapGetters } from "vuex";

export default {
  name: "ItemsTableLTL",
  props: {
    tripNumber: Number
  },
  data() {
    return {
      items: [],
      fields: null,
      hu_count: [],
      dimensions: [],
      weight: [],
      contribution: [],
      nmfcCalculated: [],
      nmfc: [],
      description: [],
      areAllSelected: "not_accepted",
      haulInformation: null,
      totalHuCount: 0,
      totalWeight: 0,
      volumeAndDensity: {
        volume: 0,
        density: 0
      },
      totalsTitle: "Totals:",
      commercialValue: null,
      allowed: true,
      totalHuCountAux: [],
      totalWeightAux: 0,
      addingCommodity: false,
      deletingCommodity: false,
      isBusy: false,
      markupPercent: null,
      markupUSD: null,
      item: null,
      assignedUser: null,
      disableEmailResponse: true,
      updateTotals: 0,
      currentDepartment: null,
      addingLinearFeet: false,
      standardize: false
    };
  },
  computed: {
    ...mapGetters("login", ["showInfoDepartment"]),
    ...mapGetters("quotes/commodity", ["showItemsTableFlag", "showRecalcTrigger"])
  },
  watch: {
    async showRecalcTrigger(val) {
      if (val > 0) await this.standardizeContributionAndNmfc();
    },
    areAllSelected: function() {
      for (let item in this.items) {
        if (this.areAllSelected === "accepted") {
          this.items[item].isChecked = "accepted";
        } else if (this.areAllSelected === "not_accepted") {
          this.items[item].isChecked = "not_accepted";
        }
      }
    },
    items: function() {
      return this.$emit("input", this.items);
    },
    async showInfoDepartment(value) {
      this.currentDepartment = value;
    }
  },
  async mounted() {
    this.isBusy = true;
    let quoteId = this.$route.params.id;
    await this.$store.dispatch("quotes/quote/getQuote", quoteId);
    await this.getItemsTable();
    let userAux = localStorage.getItem("charlie-username-otr");
    this.user = JSON.parse(userAux);
    this.currentDepartment = this.showInfoDepartment;
    this.handlerItemsTableLTL();
    this.totalHuCountAux = [];
    for (let item in this.items) {
      if (
        this.items[item].hu_count != null &&
        this.items[item].hu_count != ""
      ) {
        this.totalHuCountAux.push(this.items[item].hu_count);
      }
    }
    for (let item in this.items) {
      if (
        this.items[item].nmfcCalculated == null ||
        this.items[item].nmfcCalculated == ""
      ) {
        this.calculateNmfc(item);
      }
    }
    this.markupUSD = this.haulInformation[this.tripNumber - 1].markup;
    this.markupPercent = this.haulInformation[
      this.tripNumber - 1
    ].markup_percentage;
    await this.getAllTotals();
    this.isBusy = false;
    let flagContributions = true;
    if (this.haulInformation[this.tripNumber - 1].commodity.length > 0) {
      for (let item in this.items) {
        if (
          this.items[item].dimensions == null ||
          this.items[item].dimensions == "" ||
          this.items[item].hu_count == null ||
          this.items[item].hu_count == ""
        ) {
          flagContributions = false;
        }
      }
      if (flagContributions) {
        this.getContributions();
      }
    }
    this.$store.commit("quotes/commodity/setItemsTableFlag", true);
    return this.$emit("input", this.items);
  },
  methods: {
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    getItemsTable() {
      this.fields = fieldsTable;
      this.haulInformation = this.$store.getters[
        "quotes/quote/showQuoteById"
      ].haul;
      if (this.haulInformation[this.tripNumber - 1]) {
        for (let item in this.haulInformation[this.tripNumber - 1].commodity) {
          let newItem = {};
          newItem.isChecked = "not_accepted";
          newItem.hu_count = this.haulInformation[
            this.tripNumber - 1
          ].commodity[item].hu_count;
          newItem.dimensions = this.haulInformation[
            this.tripNumber - 1
          ].commodity[item].dimensions;
          newItem.weight = this.haulInformation[this.tripNumber - 1].commodity[
            item
          ].weight;
          newItem.contribution = this.haulInformation[
            this.tripNumber - 1
          ].commodity[item].contribution;
          newItem.nmfcCalculated = this.haulInformation[
            this.tripNumber - 1
          ].commodity[item].nmfc_calculated;
          newItem.nmfc = this.haulInformation[this.tripNumber - 1].commodity[
            item
          ].nmfc_text;
          if (
            this.haulInformation[this.tripNumber - 1].commodity[item]
              .description
          ) {
            let charAtString = this.haulInformation[
              this.tripNumber - 1
            ].commodity[item].description.toLowerCase();
            newItem.description =
              charAtString.charAt(0).toUpperCase() + charAtString.slice(1);
          } else {
            newItem.description = null;
          }
          this.items.push(newItem);
        }
      }
      this.createDatalists();
    },
    createDatalists() {
      for (let haul in this.haulInformation) {
        for (let i in this.haulInformation[haul].commodity) {
          if (
            !this.hu_count.includes(
              this.haulInformation[haul].commodity[i].hu_count
            )
          ) {
            this.hu_count.push(
              this.haulInformation[haul].commodity[i].hu_count
            );
          }
          if (
            !this.dimensions.includes(
              this.haulInformation[haul].commodity[i].dimensions
            )
          ) {
            this.dimensions.push(
              this.haulInformation[haul].commodity[i].dimensions
            );
          }
          if (
            !this.weight.includes(
              this.haulInformation[haul].commodity[i].weight
            )
          ) {
            this.weight.push(this.haulInformation[haul].commodity[i].weight);
          }
          if (
            !this.contribution.includes(
              this.haulInformation[haul].commodity[i].contribution
            )
          ) {
            this.contribution.push(
              this.haulInformation[haul].commodity[i].contribution
            );
          }
          if (
            !this.nmfcCalculated.includes(
              this.haulInformation[haul].commodity[i].nmfc_calculated
            )
          ) {
            this.nmfcCalculated.push(
              this.haulInformation[haul].commodity[i].nmfc_calculated
            );
          }
          if (
            !this.nmfc.includes(this.haulInformation[haul].commodity[i].nmfc)
          ) {
            this.nmfc.push(this.haulInformation[haul].commodity[i].nmfc);
          }
          if (
            !this.description.includes(
              this.haulInformation[haul].commodity[i].description
            )
          ) {
            this.description.push(
              this.haulInformation[haul].commodity[i].description
            );
          }
        }
      }
    },
    handlerItemsTableLTL() {
      this.item = this.$store.getters["quotes/quote/showQuoteById"];
      this.assignedUser =
        this.item.message.user == null ? null : this.item.message.user.username;
      if (
        (this.currentDepartment.rol_name == "superuser" &&
          this.assignedUser == null) ||
        this.user[0].user.username == this.assignedUser
      ) {
        this.disableEmailResponse = false;
      }
      let allowedRoutes = ["myQueue", "failed", "queue", "alerts"];
      if (!allowedRoutes.includes(this.$route.params.pathName)) {
        this.allowed = false;
      }
      if (this.allowed) {
        if (
          (this.currentDepartment.rol_name == "quoter" &&
            this.$route.params.pathName == "queue") ||
          this.disableEmailResponse ||
          this.item.message.validation_alert
        ) {
          removePointerEvents("items-table" + this.tripNumber);
        }
      } else {
        removePointerEvents("items-table" + this.tripNumber);
      }
    },
    async getAllTotals() {
      if (
        this.$route.params.pathName !== "sent" ||
        this.$route.params.pathName !== "inProcess"
      ) {
        await this.getTotalWeight();
        await this.getTotalHuCount();
        await this.getCalculateVolumeAndDensity();
      }
    },
    updateHaul() {
      let info = {};
      info.haulId = this.haulInformation[this.tripNumber - 1].id;
      info.payload = {};
      if (
        this.haulInformation[this.tripNumber - 1].linear_feet != null &&
        this.haulInformation[this.tripNumber - 1].linear_feet != ""
      ) {
        info.payload.linear_feet = this.haulInformation[
          this.tripNumber - 1
        ].linear_feet;
      } else {
        info.payload.linear_feet = "";
      }
      if (this.totalWeight != null && this.totalWeight != "") {
        info.payload.total_weight = this.totalWeight;
      } else {
        info.payload.total_weight = 0;
      }
      info.payload.total_hu_count = this.totalHuCount;
      info.payload.cbf = String(this.volumeAndDensity.volume ?? "");
      if (this.markupUSD != null && this.markupUSD != "") {
        info.payload.markup = this.markupUSD;
      } else {
        info.payload.markup = 0;
      }
      if (this.markup_percentage != null && this.markup_percentage != "") {
        info.payload.markup_percentage = this.markupPercent;
      } else {
        info.payload.markup_percentage = 0;
      }
      if (
        this.haulInformation[this.tripNumber - 1].commercial_value != null &&
        this.haulInformation[this.tripNumber - 1].commercial_value != ""
      ) {
        info.payload.commercial_value = this.haulInformation[
          this.tripNumber - 1
        ].commercial_value;
      } else {
        info.payload.commercial_value = "";
      }
      this.$store.dispatch("quotes/haul/updateHaul", info);
      document.activeElement.blur();
    },
    async addItem() {
      this.addingCommodity = true;
      let info = {};
      info.haul_ = this.haulInformation[this.tripNumber - 1].id;
      info.hu_count = "";
      info.dimensions = "";
      info.weight = "";
      let response = await this.$store.dispatch(
        "quotes/commodity/addCommodity",
        info
      );
      if (response.status == 200) {
        let quoteId = this.$route.params.id;
        await this.$store.dispatch("quotes/quote/getQuote", quoteId);
        this.items = [];
        await this.getItemsTable();
        this.getContributions();
        this.$store.commit("quotes/commodity/setItemsTableFlag", false);
      }
      this.addingCommodity = false;
    },
    async updateCommodity(i, type) {
      if (this.items[i][type]) {
        let info = {};
        info.commodityId = this.haulInformation[this.tripNumber - 1].commodity[
          i
        ].id;
        info.payload = {};
        if (type == "dimensions") {
          info.payload.dimensions = await this.addDimensions(i);
        }
        if (type == "weight") {
          info.payload.weight = await this.addWeight(i);
        }
        if (type == "hu_count") {
          info.payload.hu_count = await this.addHuCount(i);
        }
        if (type === "description") {
          if (this.items[i].description) {
            this.items[i].description = this.items[i].description.toLowerCase();
            this.items[i].description =
              this.items[i].description.charAt(0).toUpperCase() +
              this.items[i].description.slice(1);
          } else {
            this.items[i].description = "";
          }
        }
        info.payload.nmfc_calculated = this.items[i].nmfcCalculated;
        info.payload.nmfc_text = this.items[i].nmfc;
        if (this.items[i].description) {
          info.payload.description = this.items[i].description;
        } else {
          info.payload.description = "";
        }
        info.payload.contribution = this.items[i].contribution;
        await this.$store.dispatch("quotes/commodity/updateCommodity", info);
      }
    },
    async deleteRows() {
      this.deletingCommodity = true;
      for (let i in this.items) {
        if (this.items[i].isChecked == "accepted") {
          let commodityId = this.haulInformation[this.tripNumber - 1].commodity[
            i
          ].id;
          await this.$store.dispatch(
            "quotes/commodity/deleteCommodity",
            commodityId
          );
          this.items[i].isChecked == "not_accepted";
        }
      }
      if (this.areAllSelected == "accepted") {
        this.areAllSelected = "not_accepted";
      }
      let quoteId = this.$route.params.id;
      await this.$store.dispatch("quotes/quote/getQuote", quoteId);
      this.items = [];
      this.getItemsTable();
      await this.getTotalHuCount();
      await this.getTotalWeight();
      await this.getCalculateVolumeAndDensity();
      this.getContributions();
      this.deletingCommodity = false;
    },
    async addHuCount(i) {
      let response;
      if (this.items[i].hu_count != null && this.items[i].hu_count != "") {
        response = await this.$store.dispatch(
          "standardize/standardizeHuCount",
          this.items[i].hu_count
        );
        if (response.status == 200) {
          this.items[i].hu_count = response.data[0];
          if (!this.hu_count.includes(this.items[i].hu_count)) {
            this.hu_count.push(this.items[i].hu_count);
          }
          await this.getTotalHuCount();
          await this.getTotalWeight();
          await this.getCalculateVolumeAndDensity();
          return response.data[0];
        }
      }
    },
    async addDimensions(i) {
      let response;
      if (this.items[i].dimensions != null && this.items[i].dimensions != "") {
        response = await this.$store.dispatch(
          "standardize/standardizeDimensions",
          this.items[i].dimensions
        );
        if (response.status == 200) {
          this.items[i].dimensions = response.data[0];
          if (!this.dimensions.includes(this.items[i].dimensions)) {
            this.dimensions.push(this.items[i].dimensions);
          }
          await this.getCalculateVolumeAndDensity();
          this.updateHaul();
          this.updateTotals = 0;
          return response.data[0];
        }
      }
    },
    async addWeight(i) {
      let response;
      let calculatedWeight;
      if (this.items[i].weight != null && this.items[i].weight != "") {
        this.items[i].weight = this.items[i].weight.replaceAll(" ", "");
        let unitsAux = this.items[i].weight.replace(/[^a-z]/gi, "");
        this.items[i].weight = this.items[i].weight.replaceAll(unitsAux, "");
        calculatedWeight = eval(this.items[i].weight) + unitsAux;
        response = await this.$store.dispatch(
          "standardize/standardizeWeight",
          calculatedWeight
        );
        if (response.status == 200) {
          this.items[i].weight = response.data[0];
          if (!this.weight.includes(this.items[i].weight)) {
            this.weight.push(this.items[i].weight);
          }
          await this.getTotalWeight();
          await this.getCalculateVolumeAndDensity();
          this.updateHaul();
          this.updateTotals = 0;
          return response.data[0];
        }
      }
    },
    async calculateNmfc() {
      this.standardize = true;
      for (let i = 0; i < this.items.length; i++) {
        let info = {};
        info.hu_count = this.items[i].hu_count;
        info.dimensions = this.items[i].dimensions;
        info.weight = this.items[i].weight;
        info.nmfc = this.items[i].nmfc;
        if (
          info.hu_count != null &&
          info.dimensions != null &&
          info.weight != null
        ) {
          let response = await this.$store.dispatch(
            "quotes/commodity/calculateNmfcClass",
            info
          );
          this.items[i].nmfcCalculated = response.data.nmfc;
          await this.updateCommodity(i, "nmfcCalculated");
        }
      }
      this.standardize = false;
    },
    extractWeightNumber(weightString) {
      if (weightString == null || weightString == "") return 0;
      const number = parseFloat(weightString.replace(/[^\d.]/g, ""));
      return isNaN(number) ? 0 : number;
    },
    async getCalculateVolumeAndDensity() {
      let hasHuCount = this.totalHuCountAux && this.totalHuCountAux.length > 0;
      const dimensionsCount = this.items.filter(
        item => item.dimensions != null && item.dimensions != ""
      ).length;
      if (!hasHuCount || dimensionsCount === 0 || dimensionsCount !== this.totalHuCountAux.length) {
        return;
      }
      this.totalWeightAux = this.extractWeightNumber(this.totalWeight);
      this.volumeAndDensity = await getCalculateVolumeAndDensityMixin(
        this.items,
        this.totalHuCountAux,
        this.totalWeightAux
      );
      if (
        this.$route.params.pathName === "myQueue" ||
        this.$route.params.pathName === "queue" ||
        this.$route.params.pathName === "failed" ||
        this.$route.params.pathName === "alerts"
      ) {
        this.updateTotals += 1;
        if (this.updateTotals == 3) {
          this.updateHaul();
        }
        this.updateTotals = 0;
      }
    },
    async getContributions() {
      this.standardize = true;
      let dimensionsAux = [];
      for (let item in this.items) {
        if (
          this.items[item].dimensions != null &&
          this.items[item].dimensions != ""
        ) {
          dimensionsAux.push(this.items[item].dimensions);
        }
      }
      let info = {};
      info.hu_count = this.totalHuCountAux;
      info.dimensions = dimensionsAux;
      info.total_volume = this.volumeAndDensity.volume;
      if (
        info.hu_count != null &&
        info.hu_count != "" &&
        info.dimensions != null &&
        info.dimensions != "" &&
        info.total_volume != null &&
        info.total_volume != ""
      ) {
        if (info.hu_count.length == info.dimensions.length) {
          let response = await this.$store.dispatch(
            "quotes/commodity/getContributions",
            info
          );
          if (response.status == 200) {
            if (response.data) {
              for (let item in this.items) {
                this.items[item].contribution = response.data[item];
                if (
                  this.$route.params.pathName === "myQueue" ||
                  this.$route.params.pathName === "failed"
                ) {
                  await this.updateCommodity(item, "contribution");
                }
              }
            }
          }
        }
      }
      this.standardize = false;
    },
    async getTotalHuCount() {
      let totalHuCountMixin = await getTotalHuCountMixin(
        this.totalHuCountAux,
        this.items,
        this.totalWeight
      );
      this.totalHuCount = totalHuCountMixin.responseAux + " HU";
      this.totalHuCountAux = totalHuCountMixin.totalHuCountAux;
      if (
        this.$route.params.pathName === "myQueue" ||
        this.$route.params.pathName === "queue" ||
        this.$route.params.pathName === "failed" ||
        this.$route.params.pathName === "alerts"
      ) {
        this.updateTotals += 1;
      }
    },
    async getTotalWeight() {
      this.totalWeight = await getTotalWeightMixin(
        this.totalHuCountAux,
        this.items
      );
      if (
        this.$route.params.pathName === "myQueue" ||
        this.$route.params.pathName === "queue" ||
        this.$route.params.pathName === "failed" ||
        this.$route.params.pathName === "alerts"
      ) {
        this.updateTotals += 1;
      }
    },
    async standardizeTotalWieght() {
      await this.getContributions();
      let info = {};
      let calculatedWeight;
      this.totalWeight = this.totalWeight.replaceAll(" ", "");
      let unitsAux = this.totalWeight.replace(/[^a-z]/gi, "");
      this.totalWeight = this.totalWeight.replaceAll(unitsAux, "");
      calculatedWeight = eval(this.totalWeight) + unitsAux;
      this.totalWeight = await standardizeTotalWieghtMixin(calculatedWeight);
      info.total_weight = this.totalWeight;
      info.contributions = [];
      for (let item in this.items) {
        if (this.items[item].contribution) {
          info.contributions.push(this.items[item].contribution);
        }
      }
      let newWeights = await this.$store.dispatch(
        "quotes/commodity/getWeightFromContribution",
        info
      );
      for (let i in newWeights.data) {
        this.items[i].weight = newWeights.data[i];
        let info = {};
        info.commodityId = this.haulInformation[this.tripNumber - 1].commodity[
          i
        ].id;
        info.payload = {};
        info.payload.weight = this.items[i].weight;
        await this.$store.dispatch("quotes/commodity/updateCommodity", info);
      }
    },
    onHidden(buttonName) {
      this.$refs[buttonName].focus();
    },
    async calculateLinearFeet() {
      this.addingLinearFeet = true;
      let info = {
        body: []
      };
      let currentAccessorials;
      let quoteId = this.$route.params.id;
      let fullQuote = await this.$store.dispatch(
        "quotes/quote/getQuote",
        quoteId
      );
      this.haulInformation = this.$store.getters[
        "quotes/quote/showQuoteById"
      ].haul;
      fullQuote.data.haul[this.tripNumber - 1].accessorial.forEach(element => {
        if (element.accessorial.name.toLowerCase() == "stackable") {
          currentAccessorials = element.accessorial.name.toLowerCase();
        }
      });
      for (let i = 0; i < this.items.length; i++) {
        let flagStackable = false;
        if (currentAccessorials === "stackable") {
          flagStackable = true;
        }
        info.body.push({
          dimensions: this.haulInformation[this.tripNumber - 1].commodity[i]
            .dimensions,
          hu_count: this.haulInformation[this.tripNumber - 1].commodity[i]
            .hu_count,
          weight: this.haulInformation[this.tripNumber - 1].commodity[i].weight,
          stackable: flagStackable
        });
        info.container = "ltl_dry_van_53";
      }
      let calculateLinearFeet = await this.$store.dispatch(
        "totals/getCalculateLinearFeetLTL",
        info
      );
      if (calculateLinearFeet.status == 200) {
        this.haulInformation[this.tripNumber - 1].linear_feet =
          calculateLinearFeet.data;
        this.updateHaul();
      }
      this.addingLinearFeet = false;
    },
    async standardizeContributionAndNmfc() {
      await this.getContributions();
      await this.calculateNmfc();
      await this.updateHaul();
      this.$store.commit("quotes/commodity/setItemsTableFlag", true);
    },
    validateItemsTable() {
      this.$store.commit("quotes/commodity/setItemsTableFlag", false);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/CSS/components/_itemsTable.scss";

.items-table {
  &__standardizer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 10px;
    &--warning-message {
      white-space: nowrap;
      font-size: 12px;
      font-weight: normal;
      margin-right: 5px;
      color: red;
    }
  }
  &__standardizer-button {
    display: flex;
    padding: 5px;
  }
}
.items-table__table__footer__total-hu--total {
  width: 80% !important;
  text-align: center !important;
}
::v-deep .table .thead-light th {
  border-radius: 0;
}
.calculate-linear-feet {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 95%;
}
.items-table__table__footer--linear-feet[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
